// ---------------------------------------------------------------------------
// Forecast Scenario Simulator: "what if" changes applied to copies of the open deals. Pure calculation in a
// sandbox — saved scenarios store only the changes; live opportunities are never modified.
// ---------------------------------------------------------------------------
import { z } from 'zod';
import { isoDate } from './validation';

export interface ScenarioDeal {
  id: string; name: string; ownerId: string; ownerName: string | null; accountName: string | null; stageName: string;
  amount: number; probability: number; gpPct: number | null; poDate: string | null; risk: string | null; brand: string | null; confidence: number | null;
}
const date = isoDate;
export const scenarioChangeSchema = z.discriminatedUnion('kind', [
  z.object({ dealId: z.string().uuid(), kind: z.literal('shift'), months: z.number().int().min(-24).max(24) }),
  z.object({ dealId: z.string().uuid(), kind: z.literal('po_date'), date }),
  z.object({ dealId: z.string().uuid(), kind: z.literal('value_pct'), pct: z.number().min(-100).max(1000) }),
  z.object({ dealId: z.string().uuid(), kind: z.literal('value'), amount: z.number().min(0).max(1e13) }),
  z.object({ dealId: z.string().uuid(), kind: z.literal('gp'), gpPct: z.number().min(-100).max(100) }),
  z.object({ dealId: z.string().uuid(), kind: z.literal('probability'), pct: z.number().min(0).max(100) }),
  z.object({ dealId: z.string().uuid(), kind: z.literal('discount'), pct: z.number().min(0).max(100) }),
  z.object({ dealId: z.string().uuid(), kind: z.literal('won') }),
  z.object({ dealId: z.string().uuid(), kind: z.literal('lost') }),
  z.object({ dealId: z.string().uuid(), kind: z.literal('exclude') }),
]);
export type ScenarioChange = z.infer<typeof scenarioChangeSchema>;
export const SCENARIO_KINDS = [
  { value: 'base', label: 'Base case' }, { value: 'best', label: 'Best case' }, { value: 'conservative', label: 'Conservative' },
  { value: 'worst', label: 'Worst case' }, { value: 'management', label: 'Management case' }, { value: 'custom', label: 'Custom' },
] as const;
export const scenarioSchema = z.object({
  name: z.string().trim().min(1, 'Name the scenario').max(120),
  kind: z.enum(['base', 'best', 'conservative', 'worst', 'management', 'custom']).optional(),
  changes: z.array(scenarioChangeSchema).max(2000),
  notes: z.preprocess((v) => (v === '' ? null : v), z.string().trim().max(2000).nullable()).optional(),
}).strict();
export const scenarioUpdateSchema = scenarioSchema.partial().strict();
export interface SavedScenario { id: string; name: string; kind: string; changes: ScenarioChange[]; notes: string | null; createdByName: string | null; updatedByName: string | null; updatedAt: string }
export interface ScenarioBase { deals: ScenarioDeal[]; scenarios: SavedScenario[]; canSave: boolean; currency: string }

export interface SimDeal extends ScenarioDeal { outcome: 'open' | 'won' | 'lost' | 'excluded'; changed: boolean }

const shiftMonths = (d: string, n: number) => {
  const x = new Date(`${d}T00:00:00Z`);
  const day = x.getUTCDate();
  x.setUTCDate(1); x.setUTCMonth(x.getUTCMonth() + n);
  const last = new Date(Date.UTC(x.getUTCFullYear(), x.getUTCMonth() + 1, 0)).getUTCDate();
  x.setUTCDate(Math.min(day, last));
  return x.toISOString().slice(0, 10);
};

/** The deals with the scenario's changes applied in order (several changes on one deal add up). */
export function applyScenario(deals: ScenarioDeal[], changes: ScenarioChange[]): SimDeal[] {
  const out = new Map<string, SimDeal>(deals.map((d) => [d.id, { ...d, outcome: 'open', changed: false }]));
  for (const c of changes) {
    const d = out.get(c.dealId);
    if (!d) continue; // A deal that is closed now, or that this user can't see.
    d.changed = true;
    switch (c.kind) {
      case 'shift': if (d.poDate) d.poDate = shiftMonths(d.poDate, c.months); break;
      case 'po_date': d.poDate = c.date; break;
      case 'value_pct': d.amount = Math.max(0, Math.round(d.amount * (1 + c.pct / 100))); break;
      case 'value': d.amount = c.amount; break;
      case 'gp': d.gpPct = c.gpPct; break;
      case 'probability': d.probability = c.pct; break;
      case 'discount': {
        const cost = d.amount * (1 - (d.gpPct ?? 0) / 100);
        d.amount = Math.round(d.amount * (1 - c.pct / 100));
        d.gpPct = d.amount > 0 && d.gpPct != null ? Math.round(((d.amount - cost) / d.amount) * 1000) / 10 : d.gpPct;
        break;
      }
      case 'won': d.outcome = 'won'; d.probability = 100; break;
      case 'lost': d.outcome = 'lost'; break;
      case 'exclude': d.outcome = 'excluded'; break;
    }
  }
  return [...out.values()];
}

export interface ForecastFigures { revenue: number; gp: number; weightedRevenue: number; weightedPipeline: number; poDeals: number; withoutGp: number }
/**
 * Forecast for a period [from, to): revenue = value of the deals whose expected PO date (else close date) falls in it;
 * GP = revenue × each deal's GP %; weighted = × win probability. Lost and excluded deals count nothing.
 */
export function forecastFor(deals: SimDeal[], from: string, to: string): ForecastFigures {
  const live = deals.filter((d) => d.outcome !== 'lost' && d.outcome !== 'excluded');
  const inPeriod = live.filter((d) => d.poDate && d.poDate >= from && d.poDate < to);
  return {
    revenue: Math.round(inPeriod.reduce((n, d) => n + d.amount, 0)),
    gp: Math.round(inPeriod.reduce((n, d) => n + (d.amount * (d.gpPct ?? 0)) / 100, 0)),
    weightedRevenue: Math.round(inPeriod.reduce((n, d) => n + (d.amount * d.probability) / 100, 0)),
    weightedPipeline: Math.round(live.reduce((n, d) => n + (d.amount * d.probability) / 100, 0)),
    poDeals: inPeriod.length,
    withoutGp: inPeriod.filter((d) => d.gpPct == null).length,
  };
}
/** Revenue in a period per group (salesperson, brand). */
export function forecastBy(deals: SimDeal[], from: string, to: string, key: (d: SimDeal) => string | null): Map<string, { revenue: number; gp: number }> {
  const m = new Map<string, { revenue: number; gp: number }>();
  for (const d of deals) {
    if (d.outcome === 'lost' || d.outcome === 'excluded' || !d.poDate || d.poDate < from || d.poDate >= to) continue;
    const k = key(d) ?? '—';
    const e = m.get(k) ?? { revenue: 0, gp: 0 };
    e.revenue += d.amount; e.gp += (d.amount * (d.gpPct ?? 0)) / 100;
    m.set(k, e);
  }
  return m;
}
/** First day of the month of a date, the next month, and the quarter around it. */
export function periodOf(month: string): { from: string; to: string; qFrom: string; qTo: string } {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const from = `${y}-${String(m).padStart(2, '0')}-01`;
  const to = shiftMonths(from, 1);
  const qStart = Math.floor((m - 1) / 3) * 3 + 1;
  const qFrom = `${y}-${String(qStart).padStart(2, '0')}-01`;
  return { from, to, qFrom, qTo: shiftMonths(qFrom, 3) };
}
